import type {
  ConnectorAppliedTarget,
  ConnectorParameterUndoPlan,
} from '../../types/parameterSetupUndo';
import {
  areSetupDocumentIdentitiesEqual,
  type SetupDocumentIdentity,
} from '../setupRevision/setupFingerprint';

export interface ConnectorTargetSelection {
  destination: ConnectorAppliedTarget['destination'];
  bindingKind: ConnectorAppliedTarget['bindingKind'];
}

/** The document a connector request belongs to — the setup identity, by its local name. */
export type ConnectorDocumentIdentity = SetupDocumentIdentity;

/** What the user asked to have applied, frozen at click time. */
export interface ConnectorTargetRequest extends ConnectorDocumentIdentity {
  target: ConnectorTargetSelection;
  setupFingerprint: string;
  setupRevisionId: number;
}

export type ConnectorTargetBlockReason =
  | 'unknown-project'
  | 'ambiguous-checkpoint'
  | 'provenance-mismatch'
  | 'checkpoint-not-undoable'
  | 'checkpoint-unusable'
  | 'document-changed';

export type ConnectorTargetDecision =
  /** Nothing is applied for this project: apply once, undo nothing. */
  | { kind: 'apply-fresh' }
  /** The applied target and setup are unchanged: do not touch the model. */
  | { kind: 'reapply-unchanged' }
  /** A different target is applied: reverse it completely, then apply once. */
  | { kind: 'undo-then-apply' }
  | { kind: 'blocked'; reason: ConnectorTargetBlockReason };

const BlockMessages: Record<ConnectorTargetBlockReason, string> = {
  'unknown-project':
    'Active Revit project identity is not available yet. Recheck this step before continuing.',
  'ambiguous-checkpoint':
    'The previously applied connector target cannot be identified, so it cannot be replaced safely. Undo the previous connector parameters in this project, then apply the new target.',
  'provenance-mismatch':
    'The recorded connector parameters belong to a different project or document, so they cannot be replaced from here. Reopen the project they were applied to, or undo them there first.',
  'checkpoint-not-undoable':
    'The previously applied connector parameters were not reverted successfully, so a new target cannot be applied on top of them. Retry the undo before changing the target.',
  'checkpoint-unusable':
    'The saved record of the applied connector parameters is damaged, so they cannot be reverted automatically. Remove the added connector parameters in Revit, then apply the connector target again.',
  'document-changed':
    'The active Revit document changed while this step was open. Recheck the connector target against the current document before continuing.',
};

export const connectorTargetBlockMessage = (reason: ConnectorTargetBlockReason): string =>
  BlockMessages[reason];

export const areConnectorDocumentIdentitiesEqual = areSetupDocumentIdentitiesEqual;

const isSameTarget = (
  left: ConnectorTargetSelection,
  right: ConnectorTargetSelection
): boolean =>
  left.destination === right.destination && left.bindingKind === right.bindingKind;

/** A plan that was already undone holds nothing left to reverse. */
const holdsAppliedParameters = (plan: ConnectorParameterUndoPlan): boolean =>
  plan.status !== 'undone';

/**
 * Decides how the requested connector target relates to the one recorded as
 * applied. Pure: it neither reads nor writes Revit, the store, or the network.
 */
export const decideConnectorTargetTransition = (
  checkpoint: ConnectorParameterUndoPlan | null,
  request: ConnectorTargetRequest
): ConnectorTargetDecision => {
  if (!request.projectKey) {
    return { kind: 'blocked', reason: 'unknown-project' };
  }

  if (!checkpoint || !holdsAppliedParameters(checkpoint)) {
    return { kind: 'apply-fresh' };
  }

  const applied = checkpoint.appliedTarget;
  if (!applied) {
    // A quarantined record without provenance is unreadable rather than merely
    // old, so the user gets the recovery it actually needs.
    return {
      kind: 'blocked',
      reason: checkpoint.status === 'unusable' ? 'checkpoint-unusable' : 'ambiguous-checkpoint',
    };
  }

  if (!areConnectorDocumentIdentitiesEqual(applied, request)) {
    return { kind: 'blocked', reason: 'provenance-mismatch' };
  }

  if (isSameTarget(applied, request.target)) {
    return applied.setupFingerprint === request.setupFingerprint
      ? { kind: 'reapply-unchanged' }
      : { kind: 'apply-fresh' };
  }

  if (checkpoint.status !== 'available') {
    // Only a target change needs the old one reversed; re-applying the same
    // target above is how a quarantined record is meant to be replaced.
    return {
      kind: 'blocked',
      reason:
        checkpoint.status === 'unusable' ? 'checkpoint-unusable' : 'checkpoint-not-undoable',
    };
  }

  return { kind: 'undo-then-apply' };
};

export const toAppliedTarget = (request: ConnectorTargetRequest): ConnectorAppliedTarget => ({
  destination: request.target.destination,
  bindingKind: request.target.bindingKind,
  projectKey: request.projectKey,
  documentTitle: request.documentTitle,
  documentPath: request.documentPath,
  setupFingerprint: request.setupFingerprint,
  setupRevisionId: request.setupRevisionId,
});
